import * as React from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import withAppListener from './WithAppListener';
import {
  Alert,
  Navigation,
  SessionTimeoutPrompt,
  SubscriptionPrompt,
} from '../components';
import { showAlert, signOut, useAuth } from '../context/authContext';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import { paths } from '../routes';
import getCookie from '../utils/getCookie';

function Index({ children }: { children: React.ReactNode }) {
  const [
    { user, modalState, alert, sessionExpired, storePreferences },
    dispatch,
  ] = useAuth();
  const location = useLocation();

  const { alert: storeAlert } = (location?.state || {}) as {
    alert: string;
  };

  // Does user has a possible plan they want to subscribe to after registration
  const hasPossiblePlan = getCookie('_pos_possible_plan');

  // Check whether user's plan has expired
  const nextChargeDate = moment(
    user?.defaultStore?.paymentPlan?.nextChargeDate,
  ).startOf('day');

  const subscriptionHasExpired =
    !nextChargeDate.isValid() ||
    nextChargeDate.diff(moment().startOf('day'), 'days', true) < -2; // We give the user 2 days grace period

  // Pages not to show subscription expired modal
  const noNeedToSubscribePage = [
    paths.UPGRADE_SUBSCRIPTION_URL_PATH,
    `${paths.VERIFY_SUBSCRIPTION_CALLBACK_URL_PATH}/`,
  ].includes(location.pathname);

  // Show store alert message if there's any
  React.useEffect(() => {
    if (storeAlert && !sessionExpired && !subscriptionHasExpired) {
      const lowStockAlertMsg = `${
        paths.INVENTORIES_URL_PATH
      }?filter=${encodeURIComponent(JSON.stringify({ status: 'Low Stock' }))}`;

      showAlert(dispatch, {
        show: true,
        message: `${storeAlert}. [Click here](${lowStockAlertMsg})`,
        type: 'INFO',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, storeAlert]); // sessionExpired not needed

  // ${modalState === 'OPENED' ? 'overflow-y-hidden' : ''}
  return (
    <div className="h-full flex flex-col">
      <Navigation
        user={user}
        onSignOut={() => signOut(dispatch, 'MANUAL')}
        isShowingTourGuide={Boolean(storePreferences?.showTourGuide)}
      />
      {/* <main className="relative flex-1 pt-[60px] h-full overflow-hidden"> */}
      <main className="flex-1 inset-x-0 bottom-0 pt-[60px] overflow-hidden bg-[#f6f8fa] h-full">
        <ErrorBoundary>
          <>
            <Alert
              onClose={() => showAlert(dispatch)}
              type={alert.type}
              show={alert.show}
              className={`!top-auto tablet:-mx-10 desktop:-mx-16 ${
                modalState === 'OPENED' ? 'z-[70] -mt-[60px]' : 'z-40'
              }`}
              message={alert.message}
            />

            {children}
          </>
        </ErrorBoundary>
      </main>

      {hasPossiblePlan && user?.defaultStore?.id && (
        <SubscriptionPrompt
          type="INIT-SUBSCRIPTION"
          plan={hasPossiblePlan}
          store={user.defaultStore}
          title="50% OFF for 3 months 🥳"
          notice="From all of us at Swiftinflow, thank you for selecting one of our plans. Subscribe and enjoy your discount for 3 months."
        />
      )}
      {/* We're having some trouble with your current billing information. Would you like to retry running your card again? Your card information is below. */}
      {/* Upgrade your plan to get the best out of Swiftinflow Retail. */}

      {subscriptionHasExpired &&
        user?.defaultStore &&
        !noNeedToSubscribePage && (
          <SubscriptionPrompt
            plan={user.defaultStore.paymentPlan.name}
            store={user.defaultStore}
            type={`${
              user.defaultStore.paymentPlan.name === 'Free'
                ? 'TRIAL-EXPIRED-SUBSCRIPTION'
                : 'PAID-EXPIRED-SUBSCRIPTION'
            }`}
            title={`${
              user.defaultStore.paymentPlan.name === 'Free'
                ? 'Your trial has expired 😔'
                : 'Your subscription has expired 😔'
            }`}
            notice={`${
              user.defaultStore.paymentPlan.name === 'Free'
                ? 'Purchase a subscription to continue to use your SwiftInFlow account.'
                : "We've not received any payment for your plan this month. Would you like to retry running the payment again? Your payment information is below."
            }`}
          />
        )}

      {sessionExpired && sessionExpired.sessionMsg && (
        <SessionTimeoutPrompt notice={sessionExpired.sessionMsg} />
      )}
    </div>
  );
}

export default withAppListener(Index);
